/**
 * Template Edit Dialog (WYSIWYG)
 *
 * Reuses the shared ColumnBuilder component (same one used by CreateBoardForm)
 * for true WYSIWYG column editing. Supports both editing existing templates
 * and creating new ones.
 */

import { useState, useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AsyncButton } from '@/components/ui';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import { sizing, radius } from '@/styles/design-tokens';
import { ColumnBuilder } from '@/components/core/Layout/ColumnBuilder';
import { getDefaultColumnColors } from '@/business/constants/column-colors';
import type { CreateBoardColumnsItem } from '@/types/generated/processor-types';
import type {
  GetBoardTemplatesTemplatesItem,
  UpdateBoardTemplateInput,
  UpdateBoardTemplateColumnsItem,
  CreateBoardTemplateInput,
} from '@/types/generated';

/* ───── Edit Mode ───── */
interface EditModeProps {
  mode: 'edit';
  template: GetBoardTemplatesTemplatesItem;
  onSave: (input: UpdateBoardTemplateInput) => void;
  onCreate?: never;
}

/* ───── Create Mode ───── */
interface CreateModeProps {
  mode: 'create';
  template?: never;
  onSave?: never;
  onCreate: (input: Omit<CreateBoardTemplateInput, 'organization_id'>) => void;
}

type TemplateEditDialogProps = (EditModeProps | CreateModeProps) & {
  onClose: () => void;
};

/**
 * For edit mode, we need to track existing column IDs and removal state
 * separately from the visual ColumnBuilder columns.
 */
interface TrackedColumn {
  id?: string;
  name: string;
  position: number;
  color: string;
  config: Record<string, unknown>;
  remove?: boolean;
}

export function TemplateEditDialog(props: TemplateEditDialogProps) {
  const { mode, onClose } = props;
  const colors = useThemeColors();
  const defaultColumnColors = getDefaultColumnColors(colors);

  const isEdit = mode === 'edit';
  const template = isEdit ? props.template : undefined;

  // ── State ──
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [isSaving, setIsSaving] = useState(false);

  // Columns: tracked with IDs for edit mode, plain for create mode
  const initialColumns = useMemo<TrackedColumn[]>(() => {
    if (template) {
      return [...template.statuses]
        .sort((a, b) => a.position - b.position)
        .map(s => ({
          id: s.id,
          name: s.name,
          position: s.position,
          color: s.color ?? '',
          config: (s.config as Record<string, unknown>) ?? {},
        }));
    }
    return [
      {
        name: 'To Do',
        position: 0,
        color: defaultColumnColors.TODO,
        config: {},
      },
      {
        name: 'In Progress',
        position: 1,
        color: defaultColumnColors.IN_PROGRESS,
        config: {},
      },
      {
        name: 'Done',
        position: 2,
        color: defaultColumnColors.DONE,
        config: {},
      },
    ];
  }, [
    template,
    defaultColumnColors.TODO,
    defaultColumnColors.IN_PROGRESS,
    defaultColumnColors.DONE,
  ]);

  const [trackedColumns, setTrackedColumns] =
    useState<TrackedColumn[]>(initialColumns);
  const [removedColumns, setRemovedColumns] = useState<TrackedColumn[]>([]);

  // The visual columns fed to ColumnBuilder (excludes removed ones)
  const visibleColumns: CreateBoardColumnsItem[] = trackedColumns
    .filter(c => !c.remove)
    .map((c, i) => ({
      name: c.name,
      position: i,
      color: c.color,
      config: c.config,
    }));

  const handleColumnsChange = (newCols: CreateBoardColumnsItem[]) => {
    // Rebuild tracked columns: match back by position mapping
    const removed = trackedColumns.filter(c => c.remove);
    const active = trackedColumns.filter(c => !c.remove);

    const updated: TrackedColumn[] = newCols.map((col, i) => {
      // Try to match an existing tracked column by the old order
      const existing =
        active[i] && active[i].name === col.name ? active[i] : undefined;
      if (existing) {
        return {
          ...existing,
          name: col.name,
          position: i,
          color: col.color ?? '',
          config: col.config ?? {},
        };
      }
      // Could be a reordered column — find by name match
      const byName = active.find(a => a.name === col.name && a.id);
      if (byName) {
        return {
          ...byName,
          name: col.name,
          position: i,
          color: col.color ?? '',
          config: col.config ?? {},
        };
      }
      // New column (no id)
      return {
        name: col.name,
        position: i,
        color: col.color ?? '',
        config: col.config ?? {},
      };
    });

    setTrackedColumns([...updated, ...removed]);
  };

  // Handle column removal: for edit mode, mark existing columns as removed instead of deleting
  const handleColumnRemove = (visualIndex: number) => {
    const active = trackedColumns.filter(c => !c.remove);
    const target = active[visualIndex];
    if (!target) return;

    if (target.id) {
      // Mark for removal (can be restored)
      setTrackedColumns(prev =>
        prev.map(c => (c === target ? { ...c, remove: true } : c))
      );
      setRemovedColumns(prev => [...prev, { ...target, remove: true }]);
    } else {
      // New column: just delete
      setTrackedColumns(prev => prev.filter(c => c !== target));
    }
  };

  const handleRestoreColumn = (col: TrackedColumn) => {
    setTrackedColumns(prev =>
      prev.map(c => (c.id === col.id ? { ...c, remove: false } : c))
    );
    setRemovedColumns(prev => prev.filter(c => c.id !== col.id));
  };

  // Validation
  const hasEmptyColumnNames = visibleColumns.some(c => !c.name.trim());
  const isValid =
    name.trim().length > 0 && visibleColumns.length > 0 && !hasEmptyColumnNames;

  // Save
  const handleSave = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      if (isEdit) {
        const allColumns = trackedColumns;
        const columnUpdates: UpdateBoardTemplateColumnsItem[] = allColumns.map(
          (col, idx) => ({
            id: col.id,
            name: col.name,
            position: idx,
            color: col.color || undefined,
            config: Object.keys(col.config).length > 0 ? col.config : undefined,
            remove: col.remove,
          })
        );

        const input: UpdateBoardTemplateInput = { template_id: template!.id };
        if (name !== template!.name) input.name = name;
        if (description !== (template!.description ?? ''))
          input.description = description;
        if (columnUpdates.length > 0) input.columns = columnUpdates;

        props.onSave(input);
      } else {
        props.onCreate({
          name: name.trim(),
          description: description.trim() || undefined,
          columns: visibleColumns.map((col, idx) => ({
            name: col.name.trim(),
            position: idx,
            color: col.color || undefined,
            config:
              col.config && Object.keys(col.config).length > 0
                ? col.config
                : undefined,
          })),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open && !isSaving) onClose();
      }}
    >
      <DialogContent
        className="max-w-4xl flex flex-col"
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-2 -mx-2">
            <div className="space-y-4">
              {/* Header */}
              <div>
                <h3
                  className="font-semibold text-lg mb-1"
                  style={{ color: colors.text.primary }}
                >
                  {isEdit
                    ? `Edit Template: ${template!.name}`
                    : 'Create New Template'}
                </h3>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  {isEdit
                    ? `Version ${template!.version} — saving will bump to v${template!.version + 1}`
                    : 'Define a template with columns that can be applied to new boards'}
                </p>
              </div>

              {/* Name & Description */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="template-name"
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text.primary }}
                  >
                    Template Name <span style={{ color: colors.error }}>*</span>
                  </label>
                  <input
                    id="template-name"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    maxLength={100}
                    disabled={isSaving}
                    className="w-full px-3 transition-colors"
                    style={{
                      backgroundColor: colors.offWhite,
                      borderWidth: '2px',
                      borderStyle: 'solid',
                      borderColor: colors.border.default,
                      color: colors.text.primary,
                      borderRadius: radius.md,
                      height: sizing.input.md,
                      outline: 'none',
                    }}
                    placeholder="e.g. Sprint Board"
                  />
                </div>
                <div>
                  <label
                    htmlFor="template-desc"
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text.primary }}
                  >
                    Description
                  </label>
                  <input
                    id="template-desc"
                    type="text"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    maxLength={500}
                    disabled={isSaving}
                    className="w-full px-3 transition-colors"
                    style={{
                      backgroundColor: colors.offWhite,
                      borderWidth: '2px',
                      borderStyle: 'solid',
                      borderColor: colors.border.default,
                      color: colors.text.primary,
                      borderRadius: radius.md,
                      height: sizing.input.md,
                      outline: 'none',
                    }}
                    placeholder="Optional description"
                  />
                </div>
              </div>

              {/* Columns Section — shared WYSIWYG ColumnBuilder (same as CreateBoardForm) */}
              <ColumnBuilder
                columns={visibleColumns}
                onColumnsChange={handleColumnsChange}
                onRemove={isEdit ? handleColumnRemove : undefined}
                disabled={isSaving}
                label="Columns"
              />

              {/* Removed columns (edit mode only) — can be restored */}
              {isEdit && removedColumns.length > 0 && (
                <div>
                  <p
                    className="text-xs mb-2"
                    style={{ color: colors.text.secondary }}
                  >
                    Columns marked for removal (click to restore):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {removedColumns.map(col => (
                      <button
                        key={col.id}
                        onClick={() => handleRestoreColumn(col)}
                        className="px-3 py-1 text-xs rounded-md border transition-colors hover:border-primary"
                        style={{
                          backgroundColor: colors.offWhite,
                          borderColor: colors.border.default,
                          color: colors.text.secondary,
                          textDecoration: 'line-through',
                        }}
                      >
                        {col.name}
                        <Icon
                          name={IconName.CORNER_DOWN_RIGHT}
                          size="sm"
                          className="ml-1 inline"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex justify-between pt-4 border-t mt-4"
            style={{ borderColor: colors.border.default }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 font-medium"
              style={{
                backgroundColor: colors.background,
                color: colors.text.primary,
                border: `1px solid ${colors.border.default}`,
                borderRadius: radius.DEFAULT,
                height: sizing.button.md,
                opacity: isSaving ? 0.5 : 1,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor = colors.offWhite;
                  e.currentTarget.style.borderColor = colors.text.secondary;
                }
              }}
              onMouseLeave={e => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor = colors.background;
                  e.currentTarget.style.borderColor = colors.border.default;
                }
              }}
            >
              Cancel
            </button>
            <AsyncButton
              onClick={handleSave}
              disabled={!isValid}
              isLoading={isSaving}
              icon={isEdit ? IconName.CHECK : IconName.PLUS}
              loadingText="Saving..."
              className="px-6"
            >
              {isEdit ? 'Save Changes' : 'Create Template'}
            </AsyncButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
